import Base from '@studiometa/js-toolkit';
import { gsap } from 'gsap';
import { push } from '../utils/history';
import Cache from '../utils/Cache';

const HTML = document.querySelector('html');

/**
 * @typedef {Object} FiltersRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} loader
 * @property {HTMLElement} pagination
 * @property {HTMLElement} subcategory
 * @property {HTMLElement[]} trigger
 * @property {HTMLElement} results
 * @property {HTMLElement} filter
 * @property {HTMLElement} filterOpen
 * @property {HTMLElement} filterClose
 *
 * @typedef {Object} FiltersInterface
 * @property {FiltersRefs} $refs
 */

/**
 * Filters class.
 */
export default class Filters extends Base {
  static config = {
    name: 'Filters',
    log: true,
    refs: [
      'form',
      'loader',
      'results',
      'pagination',
      'subcategory',
      'trigger[]',
      'filter',
      'filterOpen',
      'filterClose',
    ],
  };

  /**
   * A helper to parse the requests' response.
   * @type {DOMParser}
   */
  parser = new DOMParser();

  cache = new Cache({ name: 'Filters' });

  onloading = false;

  /**
   * Listen to popstate.
   */
  mounted() {
    window.addEventListener('popstate', this.onWindowPopstate);
  }

  /**
   * Listen to scroll.
   */
  scrolled() {
    // Infinite loading
    if (this.$refs.pagination) {
      const pagination =
        this.$refs.pagination.length === undefined
          ? this.$refs.pagination
          : this.$refs.pagination[this.$refs.pagination.length - 1];
      const pos = pagination.getBoundingClientRect();
      if (pos.y - window.innerHeight <= 600 && !this.onloading) {
        this.onloading = true;
        const link = pagination.querySelector('a');

        if (link) {
          const e = new Event('click');
          Object.defineProperty(e, 'target', { writable: false, value: link });
          this.onPaginationClick(e);
        }
      }
    }
  }

  /**
   * Stop to listen to popstate.
   */
  destroyed() {
    window.removeEventListener('popstate', this.onWindowPopstate);
  }

  /**
   * Update the content when an history state is changed.
   */
  onWindowPopstate() {
    this.getAndUpdateContent();
  }

  /**
   * Prevent form click
   * @param {*} event
   * @param {*} index
   * @this {Filters & FiltersInterface}
   */
  onTriggerClick() {
    setTimeout(() => {
      /**
       * Using the `dispatchEvent()` method breaks the `event.preventDefault()` declared
       * in the submit handler in some browser, we use the more recent `requestSubmit()`
       * method which trigger the form as if a submit button were clicked.
       * @link http://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/requestSubmit
       */
      if (typeof this.$refs.form.requestSubmit === 'function') {
        this.$refs.form.requestSubmit();
      } else {
        this.$refs.form.dispatchEvent(new Event('submit'));
      }
    }, 100);
  }

  /**
   * Prevent form submission.
   * @param {SubmitEvent} event
   * @this {Filters & FiltersInterface}
   */
  onFormSubmit(event) {
    this.$log('onFormSubmit', event);
    event.preventDefault();
    const { pathname } = new URL(this.$refs.form.action);
    const formData = new FormData(this.$refs.form);
    const search = Array.from(formData.entries()).reduce((acc, [key, value]) => {
      if (value !== '' && typeof value === 'string') {
        acc.append(key, value);
      }
      return acc;
    }, new URLSearchParams());

    push({ path: pathname, search });
    this.getAndUpdateContent();
    return false;
  }

  /**
   * Capture pagination clicks to navigate via ajax.
   * @param {MouseEvent} event The event object.
   */
  async onPaginationClick(event) {
    const target = event.target.nodeName === 'A' ? event.target : event.target.closest('a');

    if (!target) {
      return;
    }

    event.preventDefault();
    // HTML.classList.add('show-overlay');
    this.$refs.loader.classList.remove('opacity-0');
    const oldPathname = window.location.pathname;
    push({ path: target.pathname, search: new URLSearchParams(target.search) });
    gsap.to(this.$refs.pagination, {
      height: 0,
      duration: 0,
      onComplete: () => this.$refs.pagination.remove(),
    });
    await this.getAndUpdateContent(false);
    push({ path: oldPathname });
    this.$refs.loader.classList.add('opacity-0');

    // Scroll to start new result
    const childs = this.$refs.results.children;
    const bounds = childs[childs.length - 2].getBoundingClientRect();
    if (bounds.bottom <= window.innerHeight) {
      gsap.to(document.querySelectorAll('html, body'), {
        duration: 0.4,
        scrollTop: window.scrollY + bounds.bottom - window.innerHeight * 0.7,
        onComplete: () => {
          setTimeout(() => {
            this.onloading = false;
          }, 400);
        },
      });
    } else {
      this.onloading = false;
    }
  }

  /**
   * Get the new results.
   *
   * @param {Boolean} clear Override the content or not.
   * @this {Filters & FiltersInterface}
   */
  async getAndUpdateContent(clear = true) {
    let data = this.cache.get(window.location.href);
    if (!data) {
      const text = await fetch(window.location.href).then((response) => response.text());
      const doc = this.parser.parseFromString(text, 'text/html');
      const newForm = doc.querySelector('[data-component="Filters"] [data-ref="form"]');
      const newResults = doc.querySelector('[data-component="Filters"] [data-ref="results"]');
      data = {
        form: newForm && newForm.innerHTML,
        results: newResults && newResults.innerHTML,
      };
      this.cache.set(window.location.href, data);
    }

    if (data.results) {
      if (clear) {
        this.$refs.results.innerHTML = data.results;
      } else {
        const result = document.createElement('div');
        result.innerHTML = data.results;
        this.$refs.results.append(result);
      }
    }
    if (data.form) {
      this.$refs.form.innerHTML = data.form;
    }
    HTML.classList.remove('show-overlay');
    this.$update();
  }

  /**
   * Open/close the sub tax
   * @param {Event} e
   */
  onSubcategoryClick(e) {
    e.preventDefault();

    const next = this.$refs.subcategory.nextElementSibling;
    if (next) {
      next.classList.toggle('opacity-0');
      next.classList.toggle('invisible');
      next.classList.toggle('-translate-y-2');
    }
  }

  /**
   * Open filter
   * @param {Event} e
   */
  onFilterOpenClick(e) {
    e.preventDefault();
    this.$refs.filter.classList.remove('translate-x-full');
  }

  /**
   * Closee filter
   * @param {Event} e
   */
  onFilterCloseClick(e) {
    e.preventDefault();
    this.$refs.filter.classList.add('translate-x-full');
  }
}
